package net.sergeych.ugdx.realtime

import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import net.sergeych.mp_logger.*
import net.sergeych.mp_tools.globalLaunch
import net.sergeych.ugdx.GuestAPI
import net.sergeych.ugdx.ServiceState

/**
 * Manager for realtime channel subscriptions used to control on/off commands for multiple subscribe/unsubscribe
 * calls and to simplify channel restoration
 */
internal class SubscriptionManager(
    val name: String,
    initialCount: Int? = null,
    private val handler: suspend (Boolean) -> Unit,
) : Loggable {


    /**
     * When reconstructing channel, use it to restore subscriptions. It copies state and call handler to
     * restore subscription if need in a separate coroutine
     */
    constructor(sm: SubscriptionManager, handler: suspend (Boolean) -> Unit) : this(sm.name, sm.count, handler)

    constructor(name: String, existing: SubscriptionManager?, handler: suspend (Boolean) -> Unit)
            : this(name, existing?.count, handler)

    var count: Int = initialCount ?: 0
        private set

    val isActive: Boolean get() = count > 0

    override var logLevel: Log.Level? = Log.Level.DEBUG
    override var logTag: String = "SNM:$name"

    private suspend fun perform(on: Boolean) {
        try {
            debug { "calling handler($on)" }
            handler(on)
        } catch (t: Throwable) {
            exception { "failed to perform subscription handler($on)" to t }
        }
    }

    suspend fun subscribe(on: Boolean = true) {
        debug { "called subscribe($on)" }
        if (count < 0) warning { "called subscribe() on disconnected instance" }
        else {
            if (on)
                if (count++ == 0) perform(true)
                else
                    unsubscribe()
        }
    }

    suspend fun unsubscribe() {
        debug { "called unsubscribe()" }
        if (count > 0) {
            perform(false)
            count--
        }
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other == null || this::class != other::class) return false

        other as SubscriptionManager

        return name == other.name
    }

    override fun hashCode(): Int {
        return name.hashCode()
    }

    init {
        globalLaunch {
            access.withLock {
                if (count > 0) perform(true)
                allSubscriptions += this@SubscriptionManager
            }
        }
    }

    companion object : LogTag("SMHOST") {
        private val allSubscriptions = mutableSetOf<SubscriptionManager>()
        private val access = Mutex()

        init {
            globalLaunch {
                GuestAPI().stateFlow.collect {
                    if (it is ServiceState.SignedIn) {
                        val ss = access.withLock { allSubscriptions.toList() }
                        if (ss.isEmpty()) {
                            debug { "no subscriptions to restore" }
                        } else {
                            debug { "restring subscriptions: ${ss.size}" }
                            for (s in ss) {
                                s.handler(true)
                                debug { "re-subscribed to ${s.name}" }
                            }
                            debug { "all subscriptions restored"}
                        }
                    }
                }
            }
        }
    }
}